import { c as _c } from "react/compiler-runtime";
import FirstPage from "@mui/icons-material/FirstPage";
import LastPage from "@mui/icons-material/LastPage";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Replay from "@mui/icons-material/Replay";
import Speed from "@mui/icons-material/Speed";
import { Box, Grid, IconButton, Input, Slider, Stack, Typography, useTheme } from "@mui/material";
import { selectCallstackIsPlaying, selectCallstackLength, selectIsLastFrame, selectIsRootFrame } from "#/features/callstack/model/callstackSlice";
import { useI18nContext } from "#/shared/hooks";
import { FrameIndexLabel } from "#/shared/ui/atoms/FrameIndexLabel";
import { iconButtonHoverSx } from "#/shared/ui/styles/iconButtonHoverStyles";
import { useAppSelector } from "#/store/hooks";
export type PlayerControlsProps = {
  disabled?: boolean;
  sliderValue: number;
  setSliderValue: (value: number) => void;
  handleStepBack: () => void;
  handlePlay: () => void;
  handleReplay: () => void;
  handleStepForward: () => void;
};
export const PlayerControls: React.FC<PlayerControlsProps> = t0 => {
  const $ = _c(65);
  const {
    disabled,
    sliderValue,
    setSliderValue,
    handleStepBack,
    handlePlay,
    handleReplay,
    handleStepForward
  } = t0;
  const {
    LL
  } = useI18nContext();
  const theme = useTheme();
  const callstackLength = useAppSelector(selectCallstackLength);
  const isPlaying = useAppSelector(selectCallstackIsPlaying);
  const isRootFrame = useAppSelector(selectIsRootFrame);
  const isLastFrame = useAppSelector(selectIsLastFrame);
  let t1;
  if ($[0] !== setSliderValue) {
    t1 = (_, newValue) => {
      setSliderValue(newValue as number);
    };
    $[0] = setSliderValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSliderChange = t1;
  let t2;
  if ($[2] !== setSliderValue) {
    t2 = event => {
      const parsed = Number(event.target.value);
      if (!Number.isFinite(parsed)) {
        return;
      }
      setSliderValue(parsed);
    };
    $[2] = setSliderValue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleInputChange = t2;
  let t3;
  if ($[4] !== setSliderValue || $[5] !== sliderValue) {
    t3 = () => {
      const clamped = Math.min(700, Math.max(1, sliderValue));
      if (sliderValue !== clamped) {
        setSliderValue(clamped);
      }
    };
    $[4] = setSliderValue;
    $[5] = sliderValue;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleBlur = t3;
  const canReplay = Boolean(callstackLength && !isPlaying && isLastFrame);
  let t4;
  if ($[7] !== LL) {
    t4 = LL.PLAYBACK_INTERVAL();
    $[7] = LL;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <Typography id="input-slider" variant="caption" gutterBottom={true}>{t4}</Typography>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Grid><Speed /></Grid>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== disabled || $[13] !== handleSliderChange || $[14] !== sliderValue) {
    t7 = <Grid size="grow"><Slider disabled={disabled} value={sliderValue} min={1} max={700} onChange={handleSliderChange} aria-labelledby="input-slider" /></Grid>;
    $[12] = disabled;
    $[13] = handleSliderChange;
    $[14] = sliderValue;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== LL) {
    t8 = () => <Typography variant="caption">{LL.MS()}</Typography>;
    $[16] = LL;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t10;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      step: 5,
      min: 1,
      max: 700,
      type: "number",
      "aria-labelledby": "input-slider"
    };
    t10 = {
      width: "64px"
    };
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== disabled || $[21] !== handleBlur || $[22] !== handleInputChange || $[23] !== sliderValue || $[24] !== t8) {
    t11 = <Grid><Input disabled={disabled} value={sliderValue} size="small" onChange={handleInputChange} onBlur={handleBlur} renderSuffix={t8} inputProps={t9} sx={t10} /></Grid>;
    $[20] = disabled;
    $[21] = handleBlur;
    $[22] = handleInputChange;
    $[23] = sliderValue;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t7) {
    t12 = <Grid container={true} spacing={2} alignItems="center">{t6}{t7}{t11}</Grid>;
    $[26] = t11;
    $[27] = t7;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t5) {
    t13 = <Box width="100%">{t5}{t12}</Box>;
    $[29] = t12;
    $[30] = t5;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = disabled || !callstackLength || isRootFrame;
  let t15;
  if ($[32] !== theme) {
    t15 = iconButtonHoverSx(theme);
    $[32] = theme;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <FirstPage />;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== handleStepBack || $[36] !== t14 || $[37] !== t15) {
    t17 = <IconButton title="Step back" disabled={t14} onClick={handleStepBack} sx={t15}>{t16}</IconButton>;
    $[35] = handleStepBack;
    $[36] = t14;
    $[37] = t15;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Box sx={{
      position: "absolute",
      bottom: -1,
      left: "47%",
      transform: "translate(-50%, 50%)"
    }}><FrameIndexLabel /></Box>;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const t19 = canReplay ? "Replay" : isPlaying ? "Pause" : "Play";
  const t20 = !callstackLength;
  const t21 = canReplay ? handleReplay : handlePlay;
  let t22;
  if ($[40] !== theme) {
    t22 = iconButtonHoverSx(theme);
    $[40] = theme;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== canReplay || $[43] !== isPlaying) {
    t23 = canReplay ? <Replay /> : isPlaying ? <Pause /> : <PlayArrow />;
    $[42] = canReplay;
    $[43] = isPlaying;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t19 || $[46] !== t20 || $[47] !== t21 || $[48] !== t22 || $[49] !== t23) {
    t24 = <IconButton title={t19} disabled={t20} onClick={t21} sx={t22}>{t23}</IconButton>;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  const t25 = disabled || !callstackLength || isLastFrame;
  let t26;
  if ($[51] !== theme) {
    t26 = iconButtonHoverSx(theme);
    $[51] = theme;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <LastPage />;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== handleStepForward || $[55] !== t25 || $[56] !== t26) {
    t28 = <IconButton title="Step forward" disabled={t25} onClick={handleStepForward} sx={t26}>{t27}</IconButton>;
    $[54] = handleStepForward;
    $[55] = t25;
    $[56] = t26;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t17 || $[59] !== t24 || $[60] !== t28) {
    t29 = <Stack position="relative" direction="row" alignItems="center" spacing={0.5}>{t17}{t18}{t24}{t28}</Stack>;
    $[58] = t17;
    $[59] = t24;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t13 || $[63] !== t29) {
    t30 = <Stack py={1} px={2} direction="row" alignItems="center" justifyContent="flex-end" spacing={1} onMouseDown={_temp}>{t13}{t29}</Stack>;
    $[62] = t13;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  return t30;
};
function _temp(e) {
  return e.stopPropagation();
}